import axios from "./axios";
import store from "./store";

const sendRequest = ({ method, endpoint, data, withToken, query }) => {
    if (!method) method = "get";
    if (!endpoint) throw Error("Endpoint is required");

    let params = {
        l: endpoint,
        lang: store.state.lang,
        ...query
    };

    if (withToken) {
        let token = store.getters.userToken;

        if (!token) {
            throw Error("This request requires an api token");
        }

        params.token = token;
    }

    let config = {
        method,
        params
    };

    if (data) config.data = data;

    return axios(config);
};

export default {
    async fetchConfig() {
        let params = {
            l: "config",
            lang: store.state.lang
        };

        if (process.env.NODE_ENV == "development") {
            params.country = "us";
        }

        const r = await axios("/", { params });

        return {
            region: r.data.region,
            configValues: r.data.data.config
        };
    },

    async fetchSeries() {
        let params = {
            l: "series",
            lang: store.state.lang
        };

        let token = store.getters.userToken;

        if (token) {
            params.token = token;
        }

        const r = await axios("/", {
            params
        });

        return r.data.data.series;
    },

    async fetchSerie(id) {
        let params = {
            l: "series",
            serie_id: id,
            lang: store.state.lang
        };

        let token = store.getters.userToken;

        if (token) {
            params.token = token;
        }

        const r = await axios("/", {
            params
        });

        return r.data.data.serie;
    },

    async fetchCategories() {
        const r = await axios("/", {
            params: {
                l: "categories",
                lang: store.state.lang
            }
        });

        return r.data.data.categories;
    },

    async fetchLiveData() {
        axios.defaults.headers["_ga"] = store.state._ga;
        let params = {
            l: "v2/live",
            lang: store.state.lang
        };
        let token = store.getters.userToken;

        if (token) {
            params.token = token;
        }
        const r = await axios("/", {
            params
        });

        return r.data.live;
    },

    async fetchInitialData() {
        axios.defaults.headers["_ga"] = store.state._ga;
        let params = {
            l: "v2/init",
            lang: store.state.lang
        };
        let token = store.getters.userToken;

        if (token) {
            params.token = token;
        }
        const r = await axios("/", {
            params
        });

        return r.data.data;
    },

    async fetchGames() {
        const r = await axios("/", {
            params: {
                l: "juegos",
                lang: store.state.lang
            }
        });

        return r.data.data.juegos;
    },

    async login(email, password) {
        const formData = new FormData();

        formData.append("username", email);
        formData.append("pass", password);

        const r = await axios.post("/", formData, {
            params: {
                l: "login",
                lang: store.state.lang
            }
        });

        return r.data;
    },

    async regPosLive(options) {
        options.token = store.getters.userToken;
        await axios.post("/", options, {
            params: {
                l: "regpos",
                lang: store.state.lang
            }
        });
    },

    async registerUser(name, email, password) {
        const formData = new FormData();

        formData.append("name", name);
        formData.append("email", email);
        formData.append("password", password);

        const r = await axios.post("/", formData, {
            params: {
                l: "register",
                lang: store.state.lang
            }
        });

        return r.data;
    },

    async payment(
        plan,
        nameOnCard,
        cardNumber,
        cardExpiryMonth,
        cardExpiryYear,
        cardCVC,
        zipCode,
        promoCode = null
    ) {
        let token = store.getters.userToken;

        if (!token) {
            throw Error("Cannot dispatch payment without an api token");
        }

        const formData = new FormData();

        formData.append("token", token);
        formData.append("cycle", plan);
        formData.append("nameoncard", nameOnCard);
        formData.append("cardnumber", cardNumber);
        formData.append("expiry_month", cardExpiryMonth);
        formData.append("expiry_year", cardExpiryYear);
        formData.append("cvc", cardCVC);
        formData.append("zip_code", zipCode);
        formData.append("braintree_code", promoCode);

        const r = await axios.post("/", formData, {
            params: {
                l: "payment",
                lang: store.state.lang
            }
        });

        return r.data;
    },

    async fetchContentPages() {
        const r = await axios("/", {
            params: {
                l: "terms",
                lang: store.state.lang
            }
        });

        return r.data.data.terms;
    },

    async updateUserField(field, value) {
        let token = store.getters.userToken;

        if (!token) {
            throw Error("Cannot update user data without an api token");
        }

        const formData = new FormData();

        formData.append("key", field);
        formData.append("value", value);
        formData.append("token", token);

        const r = await axios.post("/", formData, {
            params: {
                l: "updateuser",
                lang: store.state.lang
            }
        });

        return r.data;
    },

    async contact(name, email, message) {
        const formData = new FormData();

        formData.append("name", name);
        formData.append("email", email);
        formData.append("message", message);

        const r = await axios.post("/", formData, {
            params: {
                l: "contact",
                lang: store.state.lang
            }
        });

        return r.data;
    },

    async braintreeToken() {
        let token = store.getters.userToken;

        if (!token) {
            throw Error("Cannot get braintree token without an api token");
        }

        const formData = new FormData();
        formData.append("token", token);

        const r = await axios.post("/", formData, {
            params: {
                l: "bttoken"
            }
        });

        return r.data.bttoken;
    },

    async validatePromocode(promoCode) {
        const formData = new FormData();
        formData.append("braintree_code", promoCode);

        const r = await sendRequest({
            method: "post",
            endpoint: "promocode_verify",
            data: formData
        });

        return r.data;
    },

    async applyPromocode(promoCode) {
        const formData = new FormData();
        formData.append("braintree_code", promoCode);

        const r = await sendRequest({
            method: "post",
            endpoint: "promocode",
            data: formData,
            withToken: true
        });

        return r.data;
    },

    async cancelSubscription() {
        const r = await sendRequest({
            method: "post",
            endpoint: "cancel",
            withToken: true,
            query: {
                Cancelacion: 1
            }
        });

        return r.data;
    },

    async regionLockContact(email, country) {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("country", country);

        const r = await sendRequest({
            method: "post",
            endpoint: "not_us",
            data: formData
        });

        return r.data;
    },

    async recoverPassword(email) {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("ForgotPassword", true);

        const r = await sendRequest({
            method: "post",
            endpoint: "recoverpass",
            data: formData
        });

        return r.data;
    },

    async resetPassword(email, password, hash) {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("password", password);

        // confirm password inputs are not considered that useful anymore from an UX view point
        // since our api enforces the confirmpassword field we just repeat what's in the original field
        formData.append("confirmpassword", password);

        formData.append("q", hash);
        formData.append("ResetPasswordForm", true);

        const r = await sendRequest({
            method: "post",
            endpoint: "reset",
            data: formData
        });

        return r.data;
    }
};
